import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import {
    ArrowLeft,
    History as HistoryIcon,
    Upload,
    FileSpreadsheet,
    X,
    User,
    RefreshCw,
    Search,
    ChevronLeft,
    ChevronRight,
    CalendarDays
} from "lucide-react";

import {
    ResponsiveContainer,
    LineChart,
    Line,
    XAxis,
    YAxis,
    Tooltip,
    CartesianGrid
} from "recharts";

import api from "../services/api";


function History() {
    const navigate = useNavigate();


    // ========================================
    // ACCESS
    // ========================================

    const [userRole, setUserRole] = useState("");
    const [permissions, setPermissions] = useState([]);


    // ========================================
    // TABS
    // ========================================

    const [activeTab, setActiveTab] =
        useState("prices");


    // ========================================
    // HISTORY DATA
    // ========================================

    const [priceHistory, setPriceHistory] =
        useState([]);

    const [importHistory, setImportHistory] =
        useState([]);


    // ========================================
    // LOADING / ERROR
    // ========================================

    const [loading, setLoading] =
        useState(true);

    const [priceLoading, setPriceLoading] =
        useState(false);

    const [error, setError] =
        useState("");


    // ========================================
    // PRICE FILTERS
    // ========================================

    const [search, setSearch] =
        useState("");

    const [fromDate, setFromDate] =
        useState("");

    const [toDate, setToDate] =
        useState("");

    const [selectedRange, setSelectedRange] =
        useState("ALL");


    // ========================================
    // PAGINATION
    // ========================================

    const [page, setPage] =
        useState(1);

    const [totalPages, setTotalPages] =
        useState(1);

    const [totalRecords, setTotalRecords] =
        useState(0);

    const limit = 20;


    // ========================================
    // PRICE TREND GRAPH
    // ========================================

    const [trendData, setTrendData] =
        useState(null);

    const [trendLoading, setTrendLoading] =
        useState(false);


    // ========================================
    // IMPORT ERROR POPUP
    // ========================================

    const [selectedImport, setSelectedImport] =
        useState(null);

    const [importErrors, setImportErrors] =
        useState([]);

    const [loadingErrors, setLoadingErrors] =
        useState(false);


    // ========================================
    // ACCESS HELPERS
    // ========================================

    const isOwner =
        userRole === "OWNER";


    const hasPermission = (key) => {
        if (isOwner) {
            return true;
        }

        return permissions.some(
            permission =>
                permission.permission_key === key
        );
    };


    // ========================================
    // LOAD ACCESS
    // ========================================

    const loadAccess = async () => {
        const response =
            await api.get("/auth/me");

        setUserRole(
            response.data.user?.role || ""
        );

        setPermissions(
            response.data.permissions || []
        );

        return response.data.user?.role;
    };


    // ========================================
    // DATE FORMAT FOR API
    // ========================================

    const formatDateForInput = (date) => {
        const year =
            date.getFullYear();

        const month =
            String(
                date.getMonth() + 1
            ).padStart(2, "0");

        const day =
            String(
                date.getDate()
            ).padStart(2, "0");

        return `${year}-${month}-${day}`;
    };


    // ========================================
    // QUICK DATE FILTERS
    // ========================================

    const applyQuickRange = (range) => {
        const today =
            new Date();

        let start = null;


        if (range === "7_DAYS") {
            start =
                new Date(today);

            start.setDate(
                start.getDate() - 7
            );
        }


        if (range === "30_DAYS") {
            start =
                new Date(today);

            start.setDate(
                start.getDate() - 30
            );
        }


        if (range === "6_MONTHS") {
            start =
                new Date(today);

            start.setMonth(
                start.getMonth() - 6
            );
        }


        if (range === "1_YEAR") {
            start =
                new Date(today);

            start.setFullYear(
                start.getFullYear() - 1
            );
        }


        if (range === "ALL") {
            setFromDate("");
            setToDate("");

            setSelectedRange("ALL");
            setPage(1);

            return;
        }


        setFromDate(
            formatDateForInput(start)
        );

        setToDate(
            formatDateForInput(today)
        );

        setSelectedRange(range);
        setPage(1);
    };


    // ========================================
    // CUSTOM DATE RANGE
    // ========================================

    const handleFromDate = (value) => {
        setFromDate(value);

        setSelectedRange(
            "CUSTOM"
        );

        setPage(1);
    };


    const handleToDate = (value) => {
        setToDate(value);

        setSelectedRange(
            "CUSTOM"
        );

        setPage(1);
    };


    // ========================================
    // CLEAR FILTERS
    // ========================================

    const clearFilters = () => {
        setSearch("");
        setFromDate("");
        setToDate("");

        setSelectedRange(
            "ALL"
        );

        setPage(1);

        setTrendData(null);
    };


    // ========================================
    // LOAD PRICE TREND GRAPH
    // ========================================

    const loadPriceTrend = async (
        productId,
        selectedFrom = "",
        selectedTo = ""
    ) => {
        if (!productId) {
            setTrendData(null);
            return;
        }


        try {
            setTrendLoading(true);

            const params = {
                product_id:
                    productId
            };


            if (selectedFrom) {
                params.from =
                    selectedFrom;
            }


            if (selectedTo) {
                params.to =
                    selectedTo;
            }


            const response =
                await api.get(
                    "/history/prices/trend",
                    {
                        params
                    }
                );


            setTrendData(
                response.data
            );


        } catch (error) {

            console.error(
                "Unable to load price trend",
                error
            );

            setTrendData(null);


        } finally {

            setTrendLoading(false);
        }
    };


    // ========================================
    // LOAD PRICE HISTORY
    // ========================================

    const loadPriceHistory = async (
        selectedPage = 1,
        searchText = "",
        selectedFrom = "",
        selectedTo = ""
    ) => {
        try {
            setPriceLoading(true);
            setError("");


            const params = {
                page:
                    selectedPage,

                limit
            };


            if (
                searchText &&
                searchText.trim()
            ) {
                params.name =
                    searchText.trim();
            }


            if (selectedFrom) {
                params.from =
                    selectedFrom;
            }


            if (selectedTo) {
                params.to =
                    selectedTo;
            }


            const response =
                await api.get(
                    "/history/prices",
                    {
                        params
                    }
                );


            const records =
                response.data.history || [];


            setPriceHistory(
                records
            );


            setPage(
                response.data.page || 1
            );


            setTotalPages(
                response.data.total_pages || 1
            );


            setTotalRecords(
                response.data.total_records || 0
            );


            // ========================================
            // GRAPH
            //
            // Only show graph if search results
            // belong to ONE product.
            // ========================================

            if (
                searchText.trim() &&
                records.length > 0
            ) {

                const productIds = [
                    ...new Set(
                        records
                            .map(
                                record =>
                                    record.product?.id
                            )
                            .filter(Boolean)
                    )
                ];


                if (
                    productIds.length === 1
                ) {

                    await loadPriceTrend(
                        productIds[0],
                        selectedFrom,
                        selectedTo
                    );

                } else {

                    setTrendData(null);
                }

            } else {

                setTrendData(null);
            }


        } catch (error) {

            setError(
                error.response?.data?.message ||
                "Unable to load price history"
            );


        } finally {

            setPriceLoading(false);
        }
    };


    // ========================================
    // LIVE SEARCH + FILTERING
    // ========================================

    useEffect(() => {
        if (
            activeTab !== "prices"
        ) {
            return;
        }


        const timer =
            setTimeout(() => {

                loadPriceHistory(
                    1,
                    search,
                    fromDate,
                    toDate
                );

            }, 350);


        return () => {
            clearTimeout(timer);
        };

        // eslint-disable-next-line
    }, [
        search,
        fromDate,
        toDate,
        activeTab
    ]);


    // ========================================
    // LOAD IMPORT HISTORY
    // ========================================

    const loadImportHistory =
        async () => {

            const response =
                await api.get(
                    "/history/imports"
                );


            setImportHistory(
                response.data.imports || []
            );
        };


    // ========================================
    // INITIAL LOAD
    // ========================================

    useEffect(() => {

        const loadPage =
            async () => {

                try {
                    setLoading(true);
                    setError("");


                    const role =
                        await loadAccess();


                    await loadPriceHistory(
                        1,
                        "",
                        "",
                        ""
                    );


                    if (
                        role === "OWNER"
                    ) {
                        await loadImportHistory();
                    }


                } catch (error) {

                    setError(
                        error.response?.data?.message ||
                        "Unable to load history"
                    );


                } finally {

                    setLoading(false);
                }
            };


        loadPage();

        // eslint-disable-next-line
    }, []);


    // ========================================
    // TAB CHANGE
    // ========================================

    const changeTab = (tab) => {
        setActiveTab(tab);
        setError("");


        if (
            tab === "imports" &&
            isOwner
        ) {
            loadImportHistory();
        }
    };


    // ========================================
    // LOAD FAILED IMPORT ROWS
    // ========================================

    const openImportErrors =
        async (record) => {

            if (!record.failed_rows) {
                return;
            }


            try {
                setLoadingErrors(true);
                setError("");


                const response =
                    await api.get(
                        `/history/imports/${record.id}/errors`
                    );


                setSelectedImport(
                    record
                );


                setImportErrors(
                    response.data.errors || []
                );


            } catch (error) {

                setError(
                    error.response?.data?.message ||
                    "Unable to load import errors"
                );


            } finally {

                setLoadingErrors(false);
            }
        };


    const closeImportErrors = () => {
        setSelectedImport(null);
        setImportErrors([]);
    };


    // ========================================
    // PRICE FORMAT
    // ========================================

    const formatPrice = (price) => {
        if (
            price === null ||
            price === undefined
        ) {
            return "-";
        }


        return Number(
            price
        ).toLocaleString(
            "en-US",
            {
                minimumFractionDigits: 0,
                maximumFractionDigits: 2
            }
        );
    };


    // ========================================
    // DATE FORMAT
    // ========================================

    const formatDate = (value) => {
        if (!value) {
            return "";
        }


        return new Date(
            value
        ).toLocaleString(
            "en-GB",
            {
                day: "2-digit",
                month: "short",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit"
            }
        );
    };


    // ========================================
    // GRAPH DATE
    // ========================================

    const formatGraphDate = (value) => {
        if (!value) {
            return "";
        }


        return new Date(
            value
        ).toLocaleDateString(
            "en-GB",
            {
                day: "2-digit",
                month: "short"
            }
        );
    };


    // ========================================
    // GRAPH PRICE
    // ========================================

    const compactPrice = (value) => {
        const amount =
            Number(value);


        if (
            amount >= 1000000
        ) {
            return `${(
                amount / 1000000
            ).toFixed(1)}M`;
        }


        if (
            amount >= 1000
        ) {
            return `${(
                amount / 1000
            ).toFixed(0)}k`;
        }


        return amount;
    };


    // ========================================
    // CHANGE SOURCE
    // ========================================

    const sourceLabel = (source) => {
        if (
            source ===
            "EXCEL_IMPORT"
        ) {
            return "Excel Import";
        }


        if (
            source ===
            "MANUAL"
        ) {
            return "Manual";
        }


        return source || "Unknown";
    };


    // ========================================
    // LOADING PAGE
    // ========================================

    if (loading) {
        return (
            <div className="mobile-app">

                <div className="loading-state">
                    Loading history...
                </div>

            </div>
        );
    }


    // ========================================
    // ACCESS DENIED
    // ========================================

    if (
        !isOwner &&
        !hasPermission(
            "VIEW_PRICE_HISTORY"
        )
    ) {
        return (
            <div className="mobile-app">

                <header className="page-header">

                    <button
                        className="back-button"
                        onClick={() =>
                            navigate(
                                "/dashboard"
                            )
                        }
                    >
                        <ArrowLeft
                            size={21}
                        />
                    </button>


                    <div>

                        <h1>
                            History
                        </h1>

                        <p>
                            Activity records
                        </p>

                    </div>

                </header>


                <div className="permission-denied-page">

                    <HistoryIcon
                        size={44}
                    />

                    <h2>
                        Access Denied
                    </h2>

                    <p>
                        You do not have permission
                        to view price history.
                    </p>

                </div>

            </div>
        );
    }


    return (
        <div className="mobile-app">


            {/* ========================================
                HEADER
            ======================================== */}

            <header className="page-header">

                <button
                    className="back-button"
                    onClick={() =>
                        navigate(
                            "/dashboard"
                        )
                    }
                >
                    <ArrowLeft
                        size={21}
                    />
                </button>


                <div>

                    <h1>
                        History
                    </h1>

                    <p>
                        Price and import activity
                    </p>

                </div>

            </header>


            <main className="history-content">


                {/* ========================================
                    TABS
                ======================================== */}

                {isOwner && (

                    <div className="history-tabs">


                        <button
                            className={
                                activeTab === "prices"
                                    ? "history-tab history-tab-active"
                                    : "history-tab"
                            }
                            onClick={() =>
                                changeTab(
                                    "prices"
                                )
                            }
                        >
                            <HistoryIcon
                                size={17}
                            />

                            Price Changes
                        </button>


                        <button
                            className={
                                activeTab === "imports"
                                    ? "history-tab history-tab-active"
                                    : "history-tab"
                            }
                            onClick={() =>
                                changeTab(
                                    "imports"
                                )
                            }
                        >
                            <Upload
                                size={17}
                            />

                            Imports
                        </button>

                    </div>

                )}


                {error && (

                    <div className="error-message">
                        {error}
                    </div>

                )}


                {/* ========================================
                    PRICE HISTORY
                ======================================== */}

                {activeTab === "prices" && (

                    <div>


                        {/* SEARCH */}

                        <div className="history-search">

                            <Search
                                size={19}
                            />


                            <input
                                type="text"
                                placeholder="Search product history..."
                                value={search}
                                onChange={(e) => {
                                    setSearch(
                                        e.target.value
                                    );

                                    setPage(1);
                                }}
                                autoComplete="off"
                            />


                            {search && (

                                <button
                                    type="button"
                                    onClick={() => {
                                        setSearch("");
                                        setPage(1);
                                        setTrendData(null);
                                    }}
                                >
                                    ×
                                </button>

                            )}

                        </div>


                        {/* ========================================
                            TIME FILTER
                        ======================================== */}

                        <div className="history-range-section">

                            <div className="history-filter-label">

                                <CalendarDays
                                    size={15}
                                />

                                Time Range

                            </div>


                            <div className="history-range-buttons">


                                <button
                                    className={
                                        selectedRange === "7_DAYS"
                                            ? "history-range-button range-active"
                                            : "history-range-button"
                                    }
                                    onClick={() =>
                                        applyQuickRange(
                                            "7_DAYS"
                                        )
                                    }
                                >
                                    7 Days
                                </button>


                                <button
                                    className={
                                        selectedRange === "30_DAYS"
                                            ? "history-range-button range-active"
                                            : "history-range-button"
                                    }
                                    onClick={() =>
                                        applyQuickRange(
                                            "30_DAYS"
                                        )
                                    }
                                >
                                    30 Days
                                </button>


                                <button
                                    className={
                                        selectedRange === "6_MONTHS"
                                            ? "history-range-button range-active"
                                            : "history-range-button"
                                    }
                                    onClick={() =>
                                        applyQuickRange(
                                            "6_MONTHS"
                                        )
                                    }
                                >
                                    6 Months
                                </button>


                                <button
                                    className={
                                        selectedRange === "1_YEAR"
                                            ? "history-range-button range-active"
                                            : "history-range-button"
                                    }
                                    onClick={() =>
                                        applyQuickRange(
                                            "1_YEAR"
                                        )
                                    }
                                >
                                    1 Year
                                </button>


                                <button
                                    className={
                                        selectedRange === "ALL"
                                            ? "history-range-button range-active"
                                            : "history-range-button"
                                    }
                                    onClick={() =>
                                        applyQuickRange(
                                            "ALL"
                                        )
                                    }
                                >
                                    All Time
                                </button>

                            </div>


                            <div className="history-custom-date-title">
                                Custom Range
                            </div>


                            <div className="history-date-grid">


                                <div className="history-date-field">

                                    <label>
                                        From
                                    </label>

                                    <input
                                        type="date"
                                        value={fromDate}
                                        onChange={(e) =>
                                            handleFromDate(
                                                e.target.value
                                            )
                                        }
                                    />

                                </div>


                                <div className="history-date-field">

                                    <label>
                                        To
                                    </label>

                                    <input
                                        type="date"
                                        value={toDate}
                                        onChange={(e) =>
                                            handleToDate(
                                                e.target.value
                                            )
                                        }
                                    />

                                </div>

                            </div>


                            {(search ||
                                fromDate ||
                                toDate ||
                                selectedRange !== "ALL") && (

                                <button
                                    type="button"
                                    className="history-clear-filters"
                                    onClick={
                                        clearFilters
                                    }
                                >
                                    Clear Filters
                                </button>

                            )}

                        </div>


                        {/* ========================================
                            PRICE TREND GRAPH
                        ======================================== */}

                        {search.trim() && (

                            trendLoading ? (

                                <div
                                    style={{
                                        marginBottom: "18px",
                                        padding: "18px",
                                        borderRadius: "18px",
                                        background: "#ffffff",
                                        border:
                                            "1px solid #e7e5f4"
                                    }}
                                >

                                    <div className="loading-state">
                                        Loading price trend...
                                    </div>

                                </div>

                            ) : trendData &&
                                trendData.points?.length > 1 ? (

                                <div
                                    style={{
                                        marginBottom: "18px",
                                        padding: "16px",
                                        borderRadius: "18px",
                                        background: "#ffffff",
                                        border:
                                            "1px solid #e7e5f4",
                                        boxShadow:
                                            "0 5px 18px rgba(58,55,110,0.05)"
                                    }}
                                >


                                    {/* GRAPH HEADER */}

                                    <div
                                        style={{
                                            display: "flex",
                                            justifyContent:
                                                "space-between",
                                            alignItems:
                                                "flex-start",
                                            gap: "10px",
                                            marginBottom:
                                                "10px"
                                        }}
                                    >

                                        <div>

                                            <small
                                                style={{
                                                    display:
                                                        "block",
                                                    marginBottom:
                                                        "3px",
                                                    color:
                                                        "#7d839a",
                                                    fontSize:
                                                        "9px"
                                                }}
                                            >
                                                Price Trend
                                            </small>


                                            <h3
                                                style={{
                                                    margin: 0,
                                                    fontSize:
                                                        "15px"
                                                }}
                                            >
                                                {
                                                    trendData
                                                        .product
                                                        ?.product_name
                                                }
                                            </h3>

                                        </div>


                                        <span
                                            style={{
                                                padding:
                                                    "5px 8px",
                                                borderRadius:
                                                    "20px",
                                                background:
                                                    "#f0edff",
                                                color:
                                                    "#5b4bdb",
                                                fontSize:
                                                    "8px",
                                                fontWeight:
                                                    "700"
                                            }}
                                        >
                                            {
                                                trendData
                                                    .change_count
                                            }{" "}
                                            {
                                                trendData
                                                    .change_count ===
                                                1
                                                    ? "change"
                                                    : "changes"
                                            }
                                        </span>

                                    </div>


                                    {/* GRAPH */}

                                    <div
                                        style={{
                                            width:
                                                "100%",
                                            height:
                                                "190px"
                                        }}
                                    >

                                        <ResponsiveContainer
                                            width="100%"
                                            height="100%"
                                        >

                                            <LineChart
                                                data={
                                                    trendData.points
                                                }
                                                margin={{
                                                    top: 10,
                                                    right: 8,
                                                    left: -18,
                                                    bottom: 0
                                                }}
                                            >

                                                <CartesianGrid
                                                    strokeDasharray="3 3"
                                                    vertical={
                                                        false
                                                    }
                                                    stroke="#eceaf5"
                                                />


                                                <XAxis
                                                    dataKey="changed_at"
                                                    tickFormatter={
                                                        formatGraphDate
                                                    }
                                                    axisLine={
                                                        false
                                                    }
                                                    tickLine={
                                                        false
                                                    }
                                                    minTickGap={
                                                        18
                                                    }
                                                    tick={{
                                                        fontSize:
                                                            9,
                                                        fill:
                                                            "#7d839a"
                                                    }}
                                                />


                                                <YAxis
                                                    tickFormatter={
                                                        compactPrice
                                                    }
                                                    axisLine={
                                                        false
                                                    }
                                                    tickLine={
                                                        false
                                                    }
                                                    width={45}
                                                    tick={{
                                                        fontSize:
                                                            9,
                                                        fill:
                                                            "#7d839a"
                                                    }}
                                                />


                                                <Tooltip
                                                    labelFormatter={
                                                        value =>
                                                            new Date(
                                                                value
                                                            ).toLocaleString(
                                                                "en-GB"
                                                            )
                                                    }
                                                    formatter={
                                                        value => [
                                                            `TSh ${formatPrice(
                                                                value
                                                            )}`,
                                                            "Price"
                                                        ]
                                                    }
                                                    contentStyle={{
                                                        borderRadius:
                                                            "12px",
                                                        border:
                                                            "1px solid #e7e5f4",
                                                        fontSize:
                                                            "11px"
                                                    }}
                                                />


                                                <Line
                                                    type="monotone"
                                                    dataKey="price"
                                                    stroke="#5b4bdb"
                                                    strokeWidth={
                                                        3
                                                    }
                                                    dot={{
                                                        r: 3,
                                                        fill:
                                                            "#5b4bdb",
                                                        strokeWidth:
                                                            0
                                                    }}
                                                    activeDot={{
                                                        r: 5
                                                    }}
                                                />

                                            </LineChart>

                                        </ResponsiveContainer>

                                    </div>


                                    {/* GRAPH STATS */}

                                    <div
                                        style={{
                                            marginTop:
                                                "10px",
                                            display:
                                                "grid",
                                            gridTemplateColumns:
                                                "repeat(3, 1fr)",
                                            gap:
                                                "7px"
                                        }}
                                    >


                                        <div
                                            style={{
                                                padding:
                                                    "9px 5px",
                                                borderRadius:
                                                    "11px",
                                                background:
                                                    "#f8f8fd",
                                                textAlign:
                                                    "center"
                                            }}
                                        >

                                            <span
                                                style={{
                                                    display:
                                                        "block",
                                                    color:
                                                        "#7d839a",
                                                    fontSize:
                                                        "8px",
                                                    marginBottom:
                                                        "3px"
                                                }}
                                            >
                                                Current
                                            </span>

                                            <strong
                                                style={{
                                                    fontSize:
                                                        "10px",
                                                    color:
                                                        "#5b4bdb"
                                                }}
                                            >
                                                TSh{" "}
                                                {
                                                    formatPrice(
                                                        trendData
                                                            .product
                                                            ?.current_price
                                                    )
                                                }
                                            </strong>

                                        </div>


                                        <div
                                            style={{
                                                padding:
                                                    "9px 5px",
                                                borderRadius:
                                                    "11px",
                                                background:
                                                    "#f8f8fd",
                                                textAlign:
                                                    "center"
                                            }}
                                        >

                                            <span
                                                style={{
                                                    display:
                                                        "block",
                                                    color:
                                                        "#7d839a",
                                                    fontSize:
                                                        "8px",
                                                    marginBottom:
                                                        "3px"
                                                }}
                                            >
                                                Lowest
                                            </span>

                                            <strong
                                                style={{
                                                    fontSize:
                                                        "10px",
                                                    color:
                                                        "#23915c"
                                                }}
                                            >
                                                TSh{" "}
                                                {
                                                    formatPrice(
                                                        trendData
                                                            .lowest_price
                                                    )
                                                }
                                            </strong>

                                        </div>


                                        <div
                                            style={{
                                                padding:
                                                    "9px 5px",
                                                borderRadius:
                                                    "11px",
                                                background:
                                                    "#f8f8fd",
                                                textAlign:
                                                    "center"
                                            }}
                                        >

                                            <span
                                                style={{
                                                    display:
                                                        "block",
                                                    color:
                                                        "#7d839a",
                                                    fontSize:
                                                        "8px",
                                                    marginBottom:
                                                        "3px"
                                                }}
                                            >
                                                Highest
                                            </span>

                                            <strong
                                                style={{
                                                    fontSize:
                                                        "10px",
                                                    color:
                                                        "#d15a69"
                                                }}
                                            >
                                                TSh{" "}
                                                {
                                                    formatPrice(
                                                        trendData
                                                            .highest_price
                                                    )
                                                }
                                            </strong>

                                        </div>

                                    </div>

                                </div>

                            ) : null

                        )}


                        {/* ========================================
                            PRICE HISTORY HEADING
                        ======================================== */}

                        <div className="history-section-heading">

                            <div>

                                <h2>
                                    Price Changes
                                </h2>

                                <p>
                                    {totalRecords}{" "}
                                    {
                                        totalRecords === 1
                                            ? "record"
                                            : "records"
                                    }
                                </p>

                            </div>


                            <HistoryIcon
                                size={20}
                            />

                        </div>


                        {/* ========================================
                            PRICE RESULTS
                        ======================================== */}

                        {priceLoading ? (

                            <div className="loading-state">
                                Loading price history...
                            </div>

                        ) : priceHistory.length === 0 ? (

                            <div className="empty-state">

                                <HistoryIcon
                                    size={40}
                                />

                                <h3>
                                    No price changes found
                                </h3>

                                <p>
                                    Try another product
                                    or date range.
                                </p>

                            </div>

                        ) : (

                            <div className="history-list">

                                {priceHistory.map(
                                    record => (

                                    <div
                                        className="price-history-card"
                                        key={
                                            record.id
                                        }
                                    >

                                        <div className="history-card-top">


                                            <div className="history-product-icon">

                                                <RefreshCw
                                                    size={18}
                                                />

                                            </div>


                                            <div className="history-product-info">

                                                <strong>
                                                    {
                                                        record
                                                            .product
                                                            ?.product_name ||
                                                        "Unknown Product"
                                                    }
                                                </strong>


                                                <small>
                                                    {
                                                        formatDate(
                                                            record.changed_at
                                                        )
                                                    }
                                                </small>

                                            </div>


                                            <span
                                                className={
                                                    record.change_source ===
                                                    "EXCEL_IMPORT"
                                                        ? "history-source excel-source"
                                                        : "history-source manual-source"
                                                }
                                            >
                                                {
                                                    sourceLabel(
                                                        record.change_source
                                                    )
                                                }
                                            </span>

                                        </div>


                                        <div className="price-change-row">


                                            <div>

                                                <span>
                                                    Old Price
                                                </span>

                                                <strong className="old-price">

                                                    TSh{" "}

                                                    {
                                                        formatPrice(
                                                            record.old_price
                                                        )
                                                    }

                                                </strong>

                                            </div>


                                            <div className="price-arrow">
                                                →
                                            </div>


                                            <div>

                                                <span>
                                                    New Price
                                                </span>

                                                <strong className="new-price">

                                                    TSh{" "}

                                                    {
                                                        formatPrice(
                                                            record.new_price
                                                        )
                                                    }

                                                </strong>

                                            </div>

                                        </div>


                                        <div className="history-user">

                                            <User
                                                size={13}
                                            />

                                            <span>

                                                Changed by{" "}

                                                <strong>
                                                    {
                                                        record
                                                            .changed_by
                                                            ?.full_name ||
                                                        "Unknown User"
                                                    }
                                                </strong>

                                            </span>

                                        </div>

                                    </div>

                                ))}

                            </div>

                        )}


                        {/* ========================================
                            PAGINATION
                        ======================================== */}

                        {!priceLoading &&
                            totalPages > 1 && (

                            <div className="pagination">


                                <button
                                    disabled={
                                        page <= 1
                                    }
                                    onClick={() =>
                                        loadPriceHistory(
                                            page - 1,
                                            search,
                                            fromDate,
                                            toDate
                                        )
                                    }
                                >
                                    <ChevronLeft
                                        size={19}
                                    />
                                </button>


                                <span>
                                    Page {page} of{" "}
                                    {totalPages}
                                </span>


                                <button
                                    disabled={
                                        page >=
                                        totalPages
                                    }
                                    onClick={() =>
                                        loadPriceHistory(
                                            page + 1,
                                            search,
                                            fromDate,
                                            toDate
                                        )
                                    }
                                >
                                    <ChevronRight
                                        size={19}
                                    />
                                </button>

                            </div>

                        )}

                    </div>

                )}


                {/* ========================================
                    IMPORT HISTORY
                    OWNER ONLY
                ======================================== */}

                {isOwner &&
                    activeTab === "imports" && (

                    <div>


                        <div className="history-section-heading">

                            <div>

                                <h2>
                                    Import History
                                </h2>

                                <p>
                                    {
                                        importHistory.length
                                    }{" "}
                                    imports
                                </p>

                            </div>


                            <FileSpreadsheet
                                size={20}
                            />

                        </div>


                        {importHistory.length === 0 ? (

                            <div className="empty-state">

                                <FileSpreadsheet
                                    size={40}
                                />

                                <h3>
                                    No imports yet
                                </h3>

                                <p>
                                    Excel imports
                                    will appear here.
                                </p>

                            </div>

                        ) : (

                            <div className="history-list">

                                {importHistory.map(
                                    record => (

                                    <div
                                        className="import-history-card"
                                        key={
                                            record.id
                                        }
                                    >


                                        <div className="import-history-top">

                                            <div className="import-history-icon">

                                                <FileSpreadsheet
                                                    size={20}
                                                />

                                            </div>


                                            <div className="import-history-info">

                                                <strong>
                                                    {
                                                        record.file_name
                                                    }
                                                </strong>


                                                <small>
                                                    {
                                                        formatDate(
                                                            record.imported_at
                                                        )
                                                    }
                                                </small>

                                            </div>

                                        </div>


                                        <div className="history-import-summary">


                                            <div>

                                                <span>
                                                    Rows
                                                </span>

                                                <strong>
                                                    {
                                                        record.total_rows
                                                    }
                                                </strong>

                                            </div>


                                            <div>

                                                <span>
                                                    Added
                                                </span>

                                                <strong className="summary-added">
                                                    {
                                                        record.products_added
                                                    }
                                                </strong>

                                            </div>


                                            <div>

                                                <span>
                                                    Updated
                                                </span>

                                                <strong className="summary-updated">
                                                    {
                                                        record.products_updated
                                                    }
                                                </strong>

                                            </div>


                                            <div>

                                                <span>
                                                    Same
                                                </span>

                                                <strong>
                                                    {
                                                        record
                                                            .products_unchanged
                                                    }
                                                </strong>

                                            </div>


                                            <button
                                                type="button"
                                                className={
                                                    record.failed_rows > 0
                                                        ? "history-failed-button has-failures"
                                                        : "history-failed-button"
                                                }
                                                disabled={
                                                    !record.failed_rows ||
                                                    loadingErrors
                                                }
                                                onClick={() =>
                                                    openImportErrors(
                                                        record
                                                    )
                                                }
                                            >

                                                <span>
                                                    Failed
                                                </span>

                                                <strong>
                                                    {
                                                        record.failed_rows
                                                    }
                                                </strong>


                                                {record.failed_rows > 0 && (

                                                    <small>
                                                        View
                                                    </small>

                                                )}

                                            </button>

                                        </div>


                                        <div className="history-user">

                                            <User
                                                size={13}
                                            />

                                            <span>

                                                Uploaded by{" "}

                                                <strong>
                                                    {
                                                        record
                                                            .uploaded_by
                                                            ?.full_name ||
                                                        "Unknown User"
                                                    }
                                                </strong>

                                            </span>

                                        </div>

                                    </div>

                                ))}

                            </div>

                        )}

                    </div>

                )}

            </main>


            {/* ========================================
                IMPORT ERRORS POPUP
            ======================================== */}

            {selectedImport && (

                <div
                    className="import-errors-overlay"
                    onClick={
                        closeImportErrors
                    }
                >

                    <div
                        className="import-errors-modal"
                        onClick={(e) =>
                            e.stopPropagation()
                        }
                    >


                        <div className="import-errors-header">

                            <div>

                                <p>
                                    Import Issues
                                </p>

                                <h2>
                                    {
                                        importErrors.length
                                    }{" "}
                                    Failed Rows
                                </h2>

                            </div>


                            <button
                                type="button"
                                onClick={
                                    closeImportErrors
                                }
                            >
                                <X
                                    size={19}
                                />
                            </button>

                        </div>


                        <div className="import-errors-list">

                            {importErrors.map(
                                item => (

                                <div
                                    className="import-error-item"
                                    key={
                                        item.id
                                    }
                                >

                                    <div className="import-error-row">

                                        Row{" "}
                                        {
                                            item.row_number
                                        }

                                    </div>


                                    <strong>
                                        {
                                            item.product_name ||
                                            "Unknown Product"
                                        }
                                    </strong>


                                    <p>
                                        {
                                            item.error_message
                                        }
                                    </p>


                                    {item.raw_price !== null && (

                                        <small>

                                            Value:{" "}

                                            {
                                                item.raw_price
                                            }

                                        </small>

                                    )}

                                </div>

                            ))}

                        </div>

                    </div>

                </div>

            )}

        </div>
    );
}


export default History;